
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useBlogPost, useCreateBlogPost, useUpdateBlogPost } from '@/hooks/useBlogPosts'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  ArrowLeft,
  Save,
  Eye,
  Calendar,
  Hash,
  Image,
  Type,
  Clock,
  Sparkles,
  Layout,
  Settings
} from 'lucide-react'
import { toast } from 'sonner'
import { AdvancedRichTextEditor } from '@/components/editor/AdvancedRichTextEditor'
import { AdvancedImageUpload } from '@/components/editor/AdvancedImageUpload'
import { ArticleTemplates } from '@/components/editor/ArticleTemplates'

interface ArticleFormData {
  title: string
  slug: string
  excerpt: string
  content: string
  image_url: string
  category: string
  tags: string
  read_time: number
  status: 'draft' | 'published'
}

const AdminArticleEditorPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isEditing = !!id
  const [showTemplates, setShowTemplates] = useState(!isEditing)
  
  const { data: existingPost, isLoading } = useBlogPost(id || '', { enabled: isEditing })
  const createPostMutation = useCreateBlogPost()
  const updatePostMutation = useUpdateBlogPost()
  
  const form = useForm<ArticleFormData>({
    defaultValues: {
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      category: '',
      tags: '',
      read_time: 5,
      status: 'draft'
    }
  })

  useEffect(() => {
    if (existingPost) {
      form.reset({
        title: existingPost.title,
        slug: existingPost.slug,
        excerpt: existingPost.excerpt,
        content: existingPost.content,
        image_url: existingPost.image_url,
        category: existingPost.category,
        tags: Array.isArray(existingPost.tags) ? existingPost.tags.join(', ') : existingPost.tags,
        read_time: existingPost.read_time,
        status: existingPost.status
      })
    }
  }, [existingPost, form])

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200
    const wordCount = content.split(/\s+/).length
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute))
  }

  const onSubmit = async (data: ArticleFormData) => {
    try {
      const tagsArray = data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      
      const articleData = {
        ...data,
        tags: tagsArray,
        author_id: user?.id || '',
        slug: data.slug || generateSlug(data.title),
        read_time: data.content ? estimateReadTime(data.content) : data.read_time,
        updated_at: new Date().toISOString(),
        published_at: data.status === 'published' ? new Date().toISOString() : null
      }

      if (isEditing && id) {
        await updatePostMutation.mutateAsync({ id, ...articleData })
        toast.success('Article mis à jour avec succès')
      } else {
        await createPostMutation.mutateAsync(articleData)
        toast.success('Article créé avec succès')
      }
      
      navigate('/admin/articles')
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la sauvegarde')
    }
  }

  const handleTitleChange = (title: string) => {
    form.setValue('title', title)
    if (!isEditing || !form.getValues('slug')) {
      form.setValue('slug', generateSlug(title))
    }
  }

  const handleContentChange = (content: string) => {
    form.setValue('content', content)
    form.setValue('read_time', estimateReadTime(content))
  }

  const handleImageUploaded = (url: string) => {
    form.setValue('image_url', url)
  }

  const handleImageUrlChange = (url: string) => {
    form.setValue('image_url', url)
  }

  const handleSelectTemplate = (content: string, title: string, estimatedTime: number) => {
    form.setValue('content', content)
    form.setValue('title', title)
    form.setValue('read_time', estimatedTime)
    form.setValue('slug', generateSlug(title))
    setShowTemplates(false)
    toast.success('Template appliqué avec succès!')
  }

  if (isEditing && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/articles">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour aux articles
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Modifier l\'article' : 'Nouvel Article'}
              </h1>
              <p className="text-gray-600">
                {isEditing ? 'Modifiez votre article avec l\'éditeur avancé' : 'Créez un nouveau contenu avec toutes les fonctionnalités'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <Button 
                type="button"
                variant="outline" 
                size="sm"
                onClick={() => setShowTemplates(!showTemplates)}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {showTemplates ? 'Masquer les templates' : 'Voir les templates'}
              </Button>
            )}
            {form.watch('status') === 'published' && form.watch('slug') && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/blog/${form.watch('slug')}`} target="_blank">
                  <Eye className="w-4 h-4 mr-2" />
                  Prévisualiser
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Templates Section */}
        {showTemplates && !isEditing && (
          <div className="mb-8">
            <Card>
              <CardContent className="p-6">
                <ArticleTemplates onSelectTemplate={handleSelectTemplate} />
              </CardContent>
            </Card>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Main Content */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Contenu Principal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre de l'article *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Entrez le titre de votre article"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e)
                            handleTitleChange(e.target.value)
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL (slug)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="url-de-larticle"
                          {...field}
                        />
                      </FormControl>
                      <div className="text-sm text-gray-500">
                        L'URL sera : /blog/{field.value}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="excerpt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Extrait *</FormLabel>
                      <FormControl>
                        <AdvancedRichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Résumé court de l'article (affiché dans la liste des articles)"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Layout className="w-4 h-4" />
                        Contenu de l'article * 
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          Éditeur enrichi avec IA
                        </span>
                      </FormLabel>
                      <FormControl>
                        <AdvancedRichTextEditor
                          value={field.value}
                          onChange={(value) => {
                            field.onChange(value)
                            handleContentChange(value)
                          }}
                          placeholder="Rédigez le contenu complet de votre article ici. Utilisez la barre d'outils pour la mise en forme et l'IA pour les corrections..."
                        />
                      </FormControl>
                      <div className="text-sm text-gray-500 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Temps de lecture estimé : {form.watch('read_time')} minute(s)
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Image Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Image de couverture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="image_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <AdvancedImageUpload
                          onImageUploaded={handleImageUploaded}
                          onImageUrlChange={handleImageUrlChange}
                          currentUrl={field.value}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Métadonnées et SEO
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Catégorie *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionnez une catégorie" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Communication">Communication</SelectItem>
                            <SelectItem value="Marketing">Marketing</SelectItem>
                            <SelectItem value="Digital">Digital</SelectItem>
                            <SelectItem value="Branding">Branding</SelectItem>
                            <SelectItem value="Stratégie">Stratégie</SelectItem>
                            <SelectItem value="Innovation">Innovation</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="read_time"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Temps de lecture (min)
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tags</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="ex: branding, stratégie, innovation (séparés par des virgules)"
                          {...field}
                        />
                      </FormControl>
                      <div className="text-xs text-gray-500">
                        Les tags aident à organiser et rechercher vos articles
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Statut actuel : 
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                      form.watch('status') === 'published' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {form.watch('status') === 'published' ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        form.setValue('status', 'draft')
                        form.handleSubmit(onSubmit)()
                      }}
                      disabled={createPostMutation.isPending || updatePostMutation.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Sauvegarder comme brouillon
                    </Button>
                    
                    <Button
                      type="button"
                      onClick={() => {
                        form.setValue('status', 'published')
                        form.handleSubmit(onSubmit)()
                      }}
                      disabled={createPostMutation.isPending || updatePostMutation.isPending}
                      className="bg-gradient-to-r from-pink-500 to-blue-500"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Publier l'article
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>
      </div>
    </div>
  )
}

export default AdminArticleEditorPage
